import clsx from 'clsx'
import type {PointerEvent} from 'react'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'

interface BottomDrawerCloseIndicatorProps {
    onClose: () => void
    className?: string
}

const closeOffset: number = 80
const closeVelocity: number = 0.5
const minFlickOffset: number = 20
const velocityMaxAgeMs: number = 100
const modalCloseMs: number = 400

// Черточка для закрытия свайпом в <Modal className="bottom-drawer">, ставится перед ModalHeader.
export function BottomDrawerCloseIndicator(props: BottomDrawerCloseIndicatorProps) {

    const {
        onClose,
        className,
    } = props

    const onPointerDown = useEventCallback((event: PointerEvent<HTMLDivElement>) => {
        if (!event.isPrimary || event.button !== 0) {
            return
        }
        const dialog = event.currentTarget.closest<HTMLElement>('.modal-dialog')
        if (!dialog) {
            return
        }
        event.preventDefault()

        const pointerId: number = event.pointerId
        const startY: number = event.clientY
        let offset: number = 0
        let lastY: number = startY
        let lastTime: number = event.timeStamp
        let velocity: number = 0
        const listeners = new AbortController()

        const finish = (canClose: boolean, time: number) => {
            listeners.abort()
            dialog.classList.remove('dragging')
            const isFlick: boolean = (
                offset > minFlickOffset
                && velocity > closeVelocity
                && time - lastTime < velocityMaxAgeMs
            )
            if (!canClose || (offset <= closeOffset && !isFlick)) {
                dialog.style.removeProperty('transform')
                return
            }
            setTimeout(() => dialog.style.removeProperty('transform'), modalCloseMs)
            onClose()
        }

        const onMove = (moveEvent: globalThis.PointerEvent) => {
            if (moveEvent.pointerId !== pointerId) {
                return
            }
            if (moveEvent.pointerType === 'mouse' && moveEvent.buttons === 0) {
                finish(false, moveEvent.timeStamp)
                return
            }
            const elapsed: number = moveEvent.timeStamp - lastTime
            if (elapsed > 0) {
                velocity = (moveEvent.clientY - lastY) / elapsed
            }
            lastY = moveEvent.clientY
            lastTime = moveEvent.timeStamp
            offset = Math.max(0, moveEvent.clientY - startY)
            dialog.style.transform = `translate(0, ${offset}px)`
        }

        const options = {signal: listeners.signal}
        window.addEventListener('pointermove', onMove, options)
        window.addEventListener('pointerup', upEvent => {
            if (upEvent.pointerId !== pointerId) {
                return
            }
            // Иначе Modal примет click после свайпа за клик по фону и закроет окно.
            const swallowClick = (clickEvent: MouseEvent) => {
                clickEvent.stopImmediatePropagation()
                clickEvent.preventDefault()
            }
            window.addEventListener('click', swallowClick, {capture: true, once: true})
            setTimeout(() => window.removeEventListener('click', swallowClick, {capture: true}))
            finish(true, upEvent.timeStamp)
        }, options)
        window.addEventListener('pointercancel', cancelEvent => {
            if (cancelEvent.pointerId === pointerId) {
                finish(false, cancelEvent.timeStamp)
            }
        }, options)

        dialog.classList.add('dragging')
    })

    return (
        <div
            className={clsx(
                'bottom-drawer-close-indicator d-flex justify-content-center flex-shrink-0 pt-2 pb-3',
                className
            )}
            aria-hidden
            onPointerDown={onPointerDown}
        >
            <div className="bottom-drawer-close-indicator-bar rounded-pill bg-light-gray" />
        </div>
    )
}
